import { useEffect, useMemo, useState, type FormEvent } from 'react';
import { getAttribution } from '../lib/analytics';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import { ApiError, api } from '@/api/client';
import type { Channel } from '@/api/types';
import { FocusShell } from '@/components/layout';
import { Alert, Button, Card, Field, Input, OtpInput, Select } from '@/components/ui';
import { useSession } from '@/hooks/session';
import { useLocale, useT } from '@/i18n';
import { DEFAULT_COUNTRY, countryName, countryOptions, detectCountry } from '@/lib/country';
import { useOtpTimers } from '@/features/auth/useOtpTimers';
import { useOtpDelivery } from '@/features/auth/useOtpDelivery';
import { offersWhatsApp } from '@/features/auth/channels';
import { ContactInput } from '@/features/auth/ContactInput';
import { PasswordInput } from '@/features/auth/PasswordInput';
import { looksLikePhone, maskPhone, parseEmail, parseIdentifier, parsePhone, type Parsed } from '@/features/auth/identifier';

const CONSENT_VERSION = '2026-09-21';
const MAX_ATTEMPTS = 3;
const LOCK_SECONDS = 60;
const MIN_PASSWORD = 8;

/** identify → otp → (sign-up: email → setPassword). `password` is log-in with a password. */
type Step = 'identify' | 'otp' | 'email' | 'setPassword' | 'password';

/** `/signin` logs an existing parent in; `/signup` creates the account and asks for the details it needs. */
export type AuthMode = 'signin' | 'signup';
type CodeChannel = Extract<Channel, 'whatsapp' | 'email'>;

/** Carried between /signin and /signup so a parent never types their number or email twice. */
export interface AuthPrefill {
  contact: string;
  country: string;
}
interface AuthState {
  from?: string;
  prefill?: AuthPrefill;
  /** Set when /signin found no account for `prefill`: /signup says so above the form. */
  notFound?: boolean;
}

interface OtpTarget {
  channel: CodeChannel;
  identifier: string;
  /** What "we sent a code to…" shows: the masked phone, or the plain email. */
  display: string;
}

type Translate = ReturnType<typeof useT>;

/** Plain words for the errors either step can meet; anything unexpected gets a generic line. */
const errorText = (err: ApiError, t: Translate): string => {
  switch (err.code) {
    case 'rate_limited':
      return t('auth.tooMany');
    case 'account_blocked':
      return t('auth.blocked');
    case 'channel_unavailable':
      return t('signin.channelUnavailable');
    case 'offline':
      return t('auth.offline');
    case 'otp_expired':
    case 'otp_not_found':
      return t('otp.expired');
    default:
      return t('auth.somethingWrong');
  }
};

/** The message under a field that did not parse. */
const parseError = (parsed: Parsed, t: Translate, empty: string): string | undefined => {
  if (parsed.kind !== 'invalid') return undefined;
  if (parsed.reason === 'empty') return empty;
  if (parsed.reason === 'email') return t('signup.emailError');
  if (parsed.reason === 'phoneIndia') return t('signin.phoneError');
  return t('auth.phoneInvalid');
};

/**
 * /signin and /signup render the same component at the same place in the tree, so React would keep
 * one page's half-typed state on the other. Keyed by the navigation, each visit starts clean and
 * takes its prefill from the route state.
 */
export const SignIn = ({ mode = 'signin' }: { mode?: AuthMode }) => {
  const location = useLocation();
  return <AuthForm key={`${mode}:${location.key}`} mode={mode} />;
};

const AuthForm = ({ mode }: { mode: AuthMode }) => {
  const t = useT();
  const { locale } = useLocale();
  const signup = mode === 'signup';
  const { signIn, config, account: current } = useSession();
  /** Answered without an account: this sign-up or log-in keeps the check-in and opens its result. */
  const guest = !!current?.guest;
  const whatsappOffered = offersWhatsApp(config);
  const navigate = useNavigate();
  const location = useLocation();
  const state = (location.state as AuthState | null) ?? {};
  const from = state.from ?? '/home';
  /** Invited family members are not the child's guardian; they agree to view with the parent's permission. */
  const joining = from.startsWith('/join/');

  const detected = useMemo(() => detectCountry(), []);
  const prefill = state.prefill;
  const prefillIsPhone = !!prefill && looksLikePhone(prefill.contact);

  const [step, setStep] = useState<Step>('identify');
  const [attempted, setAttempted] = useState(false);
  const [busy, setBusy] = useState(false);
  const [formError, setFormError] = useState<string | null>(null);
  // Each step judges its own fields: errors from the form before must not show on the next one.
  useEffect(() => setAttempted(false), [step]);

  // Log in: one "Mobile number or email" box.
  const [contact, setContact] = useState(prefill?.contact ?? '');
  const [contactCountry, setContactCountry] = useState(prefill?.country ?? detected ?? DEFAULT_COUNTRY);

  // Sign up: name, mobile and/or email.
  const [parentName, setParentName] = useState('');
  const [phoneRaw, setPhoneRaw] = useState(prefillIsPhone ? prefill!.contact : '');
  const [phoneCountry, setPhoneCountry] = useState(prefillIsPhone ? prefill!.country : (detected ?? DEFAULT_COUNTRY));
  const [emailRaw, setEmailRaw] = useState(prefill && !prefillIsPhone ? prefill.contact : '');
  /** Email-only sign-up: the account's country (currency, question pack). Defaults to the device's. */
  const [emailCountry, setEmailCountry] = useState(detected ?? DEFAULT_COUNTRY);
  const [changingCountry, setChangingCountry] = useState(false);
  const [consent, setConsent] = useState(false);
  const [marketingOptIn, setMarketingOptIn] = useState(false);
  /** 409 from the server: this number or email already has an account. */
  const [taken, setTaken] = useState<'phone' | 'email' | null>(null);

  const [target, setTarget] = useState<OtpTarget | null>(null);
  /** Sign-up with both: the email confirmed straight after the account exists. */
  const [secondEmail, setSecondEmail] = useState<string | undefined>(undefined);
  const [code, setCode] = useState('');
  const [devCode, setDevCode] = useState<string | undefined>(undefined);
  const [deliveryId, setDeliveryId] = useState<string | undefined>(undefined);
  const delivery = useOtpDelivery(target?.channel === 'whatsapp' ? deliveryId : undefined);
  const [emailCode, setEmailCode] = useState('');
  const [emailDevCode, setEmailDevCode] = useState<string | undefined>(undefined);
  const [newPassword, setNewPassword] = useState('');

  const [passwordContact, setPasswordContact] = useState(prefill?.contact ?? '');
  const [passwordCountry, setPasswordCountry] = useState(prefill?.country ?? detected ?? DEFAULT_COUNTRY);
  const [passwordValue, setPasswordValue] = useState('');

  const timers = useOtpTimers({ resendAfter: 30, expiresIn: 600, lockSeconds: LOCK_SECONDS, maxAttempts: MAX_ATTEMPTS });
  const [validMinutes, setValidMinutes] = useState(10);

  // ── What the parent typed ────────────────────────────────────────────────
  const contactParsed = parseIdentifier(contact, contactCountry);
  const phoneParsed = whatsappOffered && phoneRaw.trim() ? parsePhone(phoneRaw, phoneCountry) : undefined;
  const emailParsed = emailRaw.trim() ? parseEmail(emailRaw) : undefined;
  const emailOnly = signup && !phoneParsed;

  const contactError = attempted && !signup ? parseError(contactParsed, t, t('auth.contactEmpty')) : undefined;
  const nameError = attempted && signup && !parentName.trim() ? t('signup.nameError') : undefined;
  const needOne = attempted && signup && !phoneParsed && !emailParsed;
  const phoneError = attempted && phoneParsed
    ? (parseError(phoneParsed, t, '') ?? (taken === 'phone' ? t('auth.accountExists') : undefined))
    : needOne ? t('auth.needOne') : undefined;
  const emailError = attempted && emailParsed
    ? (parseError(emailParsed, t, '') ?? (taken === 'email' ? t('auth.accountExists') : undefined))
    : needOne && !whatsappOffered ? t('auth.needOne') : undefined;
  const consentError = attempted && signup && !consent ? t('signin.consentError') : undefined;

  const inputLocked = timers.expired || timers.lockedFor > 0;
  const statusIsWarning = timers.lockedFor > 0 || timers.expired || timers.attemptsLeft < MAX_ATTEMPTS;
  const statusMessage = (() => {
    if (timers.lockedFor > 0) return t('otp.locked', { seconds: timers.lockedFor });
    if (timers.expired) return t('otp.expired');
    if (timers.attemptsLeft < MAX_ATTEMPTS) {
      return timers.attemptsLeft <= 1 ? t('otp.mismatchOne') : t('otp.mismatch', { n: timers.attemptsLeft });
    }
    return target ? t('otp.sent', { phone: target.display, minutes: validMinutes }) : '';
  })();

  const goTo = (path: '/signin' | '/signup', extra: Partial<AuthState> = {}) =>
    navigate(path, { state: { from, ...extra } satisfies AuthState });

  /** Sends the first code. Returns false (and says why) when it could not. */
  const sendCode = async (channel: CodeChannel, identifier: string, display: string, extra: { intent: 'signin' | 'signup'; also?: string }) => {
    const res = await api.auth.startOtp({ channel, identifier, ...extra });
    if (res.sent === false) {
      setFormError(res.reason === 'not_on_whatsapp' ? t('signin.notOnWhatsapp') : t('signin.deliveryFailed'));
      return false;
    }
    setDevCode(res.devCode);
    setDeliveryId(res.deliveryId);
    setValidMinutes(Math.round((res.expiresInSeconds ?? 600) / 60));
    setTarget({ channel, identifier, display });
    timers.start({ resendAfter: res.resendAfterSeconds ?? 30, expiresIn: res.expiresInSeconds ?? 600 });
    setCode('');
    setStep('otp');
    return true;
  };

  // ── Log in ───────────────────────────────────────────────────────────────
  const onSubmitLogin = async (event: FormEvent) => {
    event.preventDefault();
    setAttempted(true);
    setFormError(null);
    const parsed = contactParsed;
    if (parsed.kind === 'invalid') return;
    if (parsed.kind === 'phone' && !whatsappOffered) {
      setFormError(t('signin.channelUnavailable'));
      return;
    }
    setBusy(true);
    try {
      if (parsed.kind === 'phone') {
        await sendCode('whatsapp', parsed.e164, maskPhone(parsed.e164, parsed.country), { intent: 'signin' });
      } else {
        await sendCode('email', parsed.value, parsed.value, { intent: 'signin' });
      }
    } catch (err) {
      if (!(err instanceof ApiError)) throw err;
      // No account: no code was sent. Straight to sign-up, with what they typed already filled in.
      if (err.code === 'no_account') {
        goTo('/signup', { prefill: { contact: contact.trim(), country: parsed.kind === 'phone' ? parsed.country : contactCountry }, notFound: true });
        return;
      }
      setFormError(errorText(err, t));
    } finally {
      setBusy(false);
    }
  };

  // ── Sign up ──────────────────────────────────────────────────────────────
  const onSubmitSignup = async (event: FormEvent) => {
    event.preventDefault();
    setAttempted(true);
    setFormError(null);
    setTaken(null);
    const phoneOk = !phoneParsed || phoneParsed.kind === 'phone';
    const emailOk = !emailParsed || emailParsed.kind === 'email';
    if (!parentName.trim() || !consent || (!phoneParsed && !emailParsed) || !phoneOk || !emailOk) return;

    const phone = phoneParsed?.kind === 'phone' ? phoneParsed : undefined;
    const email = emailParsed?.kind === 'email' ? emailParsed.value : undefined;
    setBusy(true);
    try {
      if (phone) {
        const ok = await sendCode('whatsapp', phone.e164, maskPhone(phone.e164, phone.country), { intent: 'signup', also: email });
        if (ok) setSecondEmail(email);
      } else if (email) {
        await sendCode('email', email, email, { intent: 'signup' });
        setSecondEmail(undefined);
      }
    } catch (err) {
      if (!(err instanceof ApiError)) throw err;
      if (err.code === 'account_exists') {
        const field = (err.details as { field?: string } | undefined)?.field;
        setTaken(field === 'also' || !phone ? 'email' : 'phone');
        return;
      }
      setFormError(errorText(err, t));
    } finally {
      setBusy(false);
    }
  };

  // ── The code ─────────────────────────────────────────────────────────────
  const onResend = async () => {
    if (!target || timers.secondsToResend > 0 || busy) return;
    setBusy(true);
    setFormError(null);
    try {
      const res = await api.auth.startOtp({ channel: target.channel, identifier: target.identifier });
      if (res.sent === false) {
        setFormError(res.reason === 'not_on_whatsapp' ? t('signin.notOnWhatsapp') : t('signin.deliveryFailed'));
        return;
      }
      setDevCode(res.devCode);
      setDeliveryId(res.deliveryId);
      timers.start({ resendAfter: res.resendAfterSeconds ?? 30, expiresIn: res.expiresInSeconds ?? 600 });
      setCode('');
    } catch (err) {
      if (!(err instanceof ApiError)) throw err;
      setFormError(errorText(err, t));
    } finally {
      setBusy(false);
    }
  };

  const backToIdentify = () => {
    setStep('identify');
    setAttempted(false);
    setTarget(null);
    setCode('');
    setDevCode(undefined);
    setDeliveryId(undefined);
    setFormError(null);
    timers.reset();
  };

  /** WhatsApp could not deliver: back to the form, asking for an email instead. */
  const onUseEmailInstead = () => {
    backToIdentify();
    if (signup) setPhoneRaw('');
    else setContact('');
    setFormError(t('auth.useEmailInstead'));
  };

  const onSubmitOtp = async (event: FormEvent) => {
    event.preventDefault();
    if (!target || inputLocked || code.length !== 6) return;
    setBusy(true);
    setFormError(null);
    try {
      const res = await api.auth.verifyOtp({
        channel: target.channel,
        identifier: target.identifier,
        code,
        ...(signup && {
          name: parentName.trim(),
          consent: { version: joining ? `${CONSENT_VERSION}+family-viewer` : CONSENT_VERSION, accepted: true as const },
          marketingOptIn,
          ...(target.channel === 'email' && { country: emailCountry }),
        }),
        ...(detected && { detectedCountry: detected }),
        attribution: getAttribution(),
      });
      signIn(res.token, res.account);
      if (signup) {
        await afterAccountCreated();
        return;
      }
      navigate(from, { replace: true });
    } catch (err) {
      if (!(err instanceof ApiError)) throw err;
      if (err.code === 'otp_invalid' || err.code === 'otp_locked') {
        timers.fail();
        setCode('');
        return;
      }
      setFormError(errorText(err, t));
    } finally {
      setBusy(false);
    }
  };

  /** Sign-up: confirm the second email if there is one, then offer a password. */
  const afterAccountCreated = async () => {
    if (!secondEmail) {
      setStep('setPassword');
      return;
    }
    try {
      const res = await api.auth.startOtp({ channel: 'email', identifier: secondEmail });
      if (res.sent === false) throw new Error('not sent');
      setEmailDevCode(res.devCode);
      setEmailCode('');
      setFormError(null);
      setStep('email');
    } catch {
      setFormError(t('signup.emailFailed'));
      setStep('setPassword');
    }
  };

  const onSubmitEmailCode = async (event: FormEvent) => {
    event.preventDefault();
    if (emailCode.length !== 6 || !secondEmail) return;
    setBusy(true);
    setFormError(null);
    try {
      const res = await api.auth.addChannel({ channel: 'email', identifier: secondEmail, code: emailCode });
      signIn(api.token.get() ?? '', res.account);
      setStep('setPassword');
    } catch (err) {
      if (!(err instanceof ApiError)) throw err;
      setEmailCode('');
      setFormError(err.code === 'identifier_taken' ? t('signup.emailTaken') : t('signup.emailCodeWrong'));
    } finally {
      setBusy(false);
    }
  };

  const onSubmitNewPassword = async (event: FormEvent) => {
    event.preventDefault();
    setAttempted(true);
    if (newPassword.length < MIN_PASSWORD) return;
    setBusy(true);
    setFormError(null);
    try {
      await api.auth.setPassword(newPassword);
      navigate(from, { replace: true });
    } catch (err) {
      if (!(err instanceof ApiError)) throw err;
      setFormError(errorText(err, t));
    } finally {
      setBusy(false);
    }
  };

  // ── Log in with a password ───────────────────────────────────────────────
  const onSubmitPassword = async (event: FormEvent) => {
    event.preventDefault();
    setFormError(null);
    const parsed = parseIdentifier(passwordContact, passwordCountry);
    if (parsed.kind === 'invalid') {
      setFormError(parseError(parsed, t, t('auth.contactEmpty')) ?? null);
      return;
    }
    setBusy(true);
    try {
      const res = await api.auth.login({ identifier: parsed.kind === 'phone' ? parsed.e164 : parsed.value, password: passwordValue });
      signIn(res.token, res.account);
      navigate(from, { replace: true });
    } catch (err) {
      if (!(err instanceof ApiError)) throw err;
      setFormError(err.code === 'unauthorized' || err.code === 'validation' ? t('signin.loginFailed') : errorText(err, t));
    } finally {
      setBusy(false);
    }
  };

  const errorAlert = formError && (
    <div className="mb-4">
      <Alert tone="warn">{formError}</Alert>
    </div>
  );

  /** Where the parent's account was found to exist: offer to log in with it. */
  const takenContact = taken === 'phone' ? phoneRaw.trim() : emailRaw.trim();
  const takenCountry = taken === 'phone' ? (phoneParsed?.kind === 'phone' ? phoneParsed.country : phoneCountry) : contactCountry;

  return (
    <FocusShell>
      <Card>
        {step === 'identify' && !signup && (
          <>
            <h1 className="mb-1 text-h2">{t('signin.loginTitle')}</h1>
            {guest && (
              <div className="my-3">
                <Alert tone="info">{t('guest.loginNotice')}</Alert>
              </div>
            )}
            <p className="mb-4 text-body text-ink-muted">{t('signin.loginSub')}</p>
            {errorAlert}
            <form className="space-y-4" noValidate onSubmit={(e) => void onSubmitLogin(e)}>
              <Field id="signin-contact" label={t('auth.contactLabel')} hint={t('auth.contactHint')} error={contactError}>
                <ContactInput mode="any" value={contact} onChange={setContact} country={contactCountry} onCountryChange={setContactCountry} />
              </Field>
              <Button type="submit" loading={busy} full>
                {t('auth.continue')}
              </Button>
            </form>
            <div className="mt-4">
              <Button
                type="button"
                variant="tertiary"
                onClick={() => {
                  setPasswordContact(contact);
                  setPasswordCountry(contactCountry);
                  setStep('password');
                  setFormError(null);
                }}
              >
                {t('signin.password')}
              </Button>
            </div>
            <p className="mt-5 border-t border-line pt-4 text-center text-body text-ink-muted">
              {t('signin.newHere')}{' '}
              <Link
                to="/signup"
                state={{ from, ...(contact.trim() && { prefill: { contact: contact.trim(), country: contactCountry } }) } satisfies AuthState}
                className="font-bold text-lagoon-600 underline-offset-4 hover:underline"
              >
                {t('signin.createAccount')}
              </Link>
            </p>
          </>
        )}

        {step === 'identify' && signup && (
          <>
            <h1 className="mb-1 text-h2">{t(guest ? 'guest.signupTitle' : 'signup.title')}</h1>
            {guest && (
              <div className="my-3">
                <Alert tone="info">{t('guest.signupNotice')}</Alert>
              </div>
            )}
            <p className="mb-4 text-body text-ink-muted">{whatsappOffered ? t('signup.subBoth') : t('signup.subEmail')}</p>

            {state.notFound && prefill && (
              <div className="mb-4">
                <Alert tone="info">{t('signup.notFound', { contact: prefill.contact })}</Alert>
              </div>
            )}
            {errorAlert}

            <form className="space-y-4" noValidate onSubmit={(e) => void onSubmitSignup(e)}>
              <Field id="signup-name" label={t('signup.name')} error={nameError}>
                <Input autoComplete="name" maxLength={120} value={parentName} onChange={(e) => setParentName(e.target.value)} />
              </Field>
              {whatsappOffered && (
                <Field id="signup-phone" label={t('signup.phone')} hint={t('signup.phoneHint')} error={phoneError}>
                  <ContactInput mode="phone" value={phoneRaw} onChange={(v) => { setPhoneRaw(v); setTaken(null); }} country={phoneCountry} onCountryChange={setPhoneCountry} />
                </Field>
              )}
              <Field id="signup-email" label={t('signup.email')} hint={t('signup.emailHint')} error={emailError}>
                <Input type="email" inputMode="email" autoComplete="email" value={emailRaw} onChange={(e) => { setEmailRaw(e.target.value); setTaken(null); }} />
              </Field>
              {taken && (
                <p className="-mt-2 text-body">
                  <Link
                    to="/signin"
                    state={{ from, prefill: { contact: takenContact, country: takenCountry } } satisfies AuthState}
                    className="font-bold text-lagoon-600 underline underline-offset-4"
                  >
                    {t('auth.loginInstead')}
                  </Link>
                </p>
              )}

              {emailOnly && emailParsed?.kind === 'email' && (
                changingCountry ? (
                  <Field id="signup-country" label={t('signin.country')} hint={t('auth.countryHint')}>
                    <Select value={emailCountry} autoComplete="country" onChange={(e) => setEmailCountry(e.target.value)}>
                      {countryOptions(locale).map((o) => (
                        <option key={o.code} value={o.code}>
                          {o.name}
                        </option>
                      ))}
                    </Select>
                  </Field>
                ) : (
                  <p className="text-caption text-ink-muted">
                    {t('auth.countryIs', { country: countryName(emailCountry, locale) })}{' '}
                    <button type="button" className="font-bold text-lagoon-600 underline underline-offset-4" onClick={() => setChangingCountry(true)}>
                      {t('auth.change')}
                    </button>
                  </p>
                )
              )}

              <div>
                <label htmlFor="signin-consent" className="flex items-start gap-3 text-body text-ink">
                  <input
                    id="signin-consent"
                    type="checkbox"
                    checked={consent}
                    onChange={(e) => setConsent(e.target.checked)}
                    aria-describedby={consentError ? 'signin-consent-error' : undefined}
                    aria-invalid={consentError ? true : undefined}
                    className="mt-1 h-5 w-5 shrink-0 rounded-sm border-sage-300 text-teal-600"
                  />
                  <span>
                    {t(joining ? 'signin.consentFamily' : 'signin.consent')}{' '}
                    <Link to="/privacy" className="text-lagoon-600 underline underline-offset-4">
                      {t('footer.privacy')}
                    </Link>
                  </span>
                </label>
                {consentError && (
                  <p id="signin-consent-error" role="alert" className="mt-1 text-caption text-alert-ink">
                    {consentError}
                  </p>
                )}
              </div>

              <label htmlFor="signin-marketing" className="flex items-start gap-3 text-body text-ink-muted">
                <input
                  id="signin-marketing"
                  type="checkbox"
                  checked={marketingOptIn}
                  onChange={(e) => setMarketingOptIn(e.target.checked)}
                  className="mt-1 h-5 w-5 shrink-0 rounded-sm border-sage-300 text-teal-600"
                />
                <span>{emailOnly ? t('signin.marketingEmail') : t('signin.marketing')}</span>
              </label>

              <Button type="submit" loading={busy} full>
                {t('signup.cta')}
              </Button>
            </form>

            <p className="mt-5 border-t border-line pt-4 text-center text-body text-ink-muted">
              {t('signup.haveAccount')}{' '}
              <Link to="/signin" state={{ from } satisfies AuthState} className="font-bold text-lagoon-600 underline-offset-4 hover:underline">
                {t('nav.signin')}
              </Link>
            </p>
          </>
        )}

        {step === 'otp' && (
          <>
            <h1 className="mb-1 text-h2">{t('otp.title')}</h1>

            {devCode && (
              <div className="mb-4">
                <Card tone="sage">
                  <p className="text-body text-ink">{t('signin.devCode', { code: devCode })}</p>
                </Card>
              </div>
            )}
            {errorAlert}

            <div className="mb-4">
              <Alert tone={statusIsWarning ? 'warn' : 'info'}>{statusMessage}</Alert>
            </div>

            {delivery !== 'idle' && (
              <div className="mb-4" aria-live="polite">
                {delivery === 'sending' && <p className="text-body text-ink-muted">{t('otp.whatsappSending')}</p>}
                {delivery === 'delivered' && <p className="text-body text-ink">{t('otp.whatsappDelivered')}</p>}
                {(delivery === 'not_on_whatsapp' || delivery === 'failed' || delivery === 'timeout') && (
                  <Alert tone="warn">
                    <span className="block">
                      {delivery === 'not_on_whatsapp' ? t('otp.whatsappNotOn') : t('otp.whatsappNotReceived')}
                    </span>
                    <Button type="button" variant="tertiary" onClick={onUseEmailInstead}>
                      {t('signin.useEmail')}
                    </Button>
                  </Alert>
                )}
              </div>
            )}

            <form className="space-y-4" onSubmit={(e) => void onSubmitOtp(e)}>
              <OtpInput label={t('otp.label')} value={code} onChange={setCode} disabled={inputLocked} error={statusIsWarning} />
              <Button type="submit" loading={busy} disabled={inputLocked || code.length !== 6} full>
                {t('otp.cta')}
              </Button>
            </form>

            <div className="mt-4 flex flex-wrap items-center justify-between gap-3">
              <Button type="button" variant="tertiary" disabled={timers.secondsToResend > 0 || busy} onClick={() => void onResend()}>
                {timers.secondsToResend > 0 ? t('otp.resendIn', { seconds: timers.secondsToResend }) : t('otp.resend')}
              </Button>
              <Button type="button" variant="tertiary" onClick={backToIdentify}>
                {target?.channel === 'email' ? t('otp.changeEmail') : t('otp.change')}
              </Button>
            </div>
          </>
        )}

        {step === 'email' && (
          <>
            <h1 className="mb-1 text-h2">{t('signup.emailVerify.title')}</h1>
            <p className="mb-4 text-body text-ink-muted">{t('signup.emailVerify.body', { email: secondEmail ?? '' })}</p>

            {emailDevCode && (
              <div className="mb-4">
                <Card tone="sage">
                  <p className="text-body text-ink">{t('signin.devCode', { code: emailDevCode })}</p>
                </Card>
              </div>
            )}
            {errorAlert}

            <form className="space-y-4" onSubmit={(e) => void onSubmitEmailCode(e)}>
              <OtpInput label={t('otp.label')} value={emailCode} onChange={setEmailCode} />
              <Button type="submit" loading={busy} disabled={emailCode.length !== 6} full>
                {t('signup.emailVerify.cta')}
              </Button>
            </form>

            <div className="mt-4">
              <Button
                type="button"
                variant="tertiary"
                onClick={() => {
                  setFormError(null);
                  setStep('setPassword');
                }}
              >
                {t('signup.emailVerify.skip')}
              </Button>
            </div>
          </>
        )}

        {step === 'setPassword' && (
          <>
            <h1 className="mb-1 text-h2">{t('signup.passwordOffer.title')}</h1>
            <p className="mb-4 text-body text-ink-muted">{t('signup.passwordOffer.body')}</p>
            {errorAlert}
            <form className="space-y-4" noValidate onSubmit={(e) => void onSubmitNewPassword(e)}>
              <Field
                id="signup-new-password"
                label={t('signin.passwordField')}
                hint={t('signup.passwordOffer.hint')}
                error={attempted && newPassword.length < MIN_PASSWORD ? t('signup.passwordError') : undefined}
              >
                <PasswordInput autoComplete="new-password" value={newPassword} onChange={(e) => setNewPassword(e.target.value)} />
              </Field>
              <Button type="submit" loading={busy} full>
                {t('signup.passwordOffer.cta')}
              </Button>
            </form>
            <div className="mt-4">
              <Button type="button" variant="tertiary" onClick={() => navigate(from, { replace: true })}>
                {t('signup.passwordOffer.skip')}
              </Button>
            </div>
          </>
        )}

        {step === 'password' && (
          <>
            <h1 className="mb-1 text-h2">{t('signin.password')}</h1>
            {errorAlert}
            <form className="space-y-4" noValidate onSubmit={(e) => void onSubmitPassword(e)}>
              <Field id="signin-password-contact" label={t('auth.contactLabel')}>
                <ContactInput mode="any" value={passwordContact} onChange={setPasswordContact} country={passwordCountry} onCountryChange={setPasswordCountry} />
              </Field>
              <Field id="signin-password" label={t('signin.passwordField')}>
                <PasswordInput autoComplete="current-password" value={passwordValue} onChange={(e) => setPasswordValue(e.target.value)} />
              </Field>
              <Button type="submit" loading={busy} full>
                {t('signin.passwordCta')}
              </Button>
            </form>
            <div className="mt-4 flex flex-wrap items-center justify-between gap-3">
              <Button
                type="button"
                variant="tertiary"
                onClick={() => {
                  setStep('identify');
                  setFormError(null);
                }}
              >
                {t('signin.useCode')}
              </Button>
              <Link to="/reset" className="inline-flex min-h-tap items-center text-label text-teal-600 underline-offset-4 hover:underline">
                {t('signin.forgotPassword')}
              </Link>
            </div>
          </>
        )}
      </Card>
    </FocusShell>
  );
};

